import React from 'react';

/**
 * Network Service for detecting network issues and implementing retry mechanisms
 */

export class NetworkService {
  static isOnline = navigator.onLine;
  static listeners = new Set();
  static retryAttempts = new Map();
  static maxRetries = 3;
  static retryDelay = 1000; // Base delay in milliseconds

  static {
    // Initialize network status listeners
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.notifyListeners('online');
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyListeners('offline');
    });
  }

  /**
   * Add a listener for network status changes
   * @param {Function} callback - Function to call when network status changes
   * @returns {Function} Cleanup function to remove the listener
   */
  static addNetworkListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of network status change
   * @param {string} status - 'online' or 'offline'
   */
  static notifyListeners(status) {
    this.listeners.forEach(callback => {
      try {
        callback(status, this.isOnline);
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
  }

  /**
   * Check if the current error is a network error
   * @param {Error} error - The error to check
   * @returns {boolean} True if it's a network error
   */
  static isNetworkError(error) {
    if (!error) return false;

    // Check for common network error indicators
    const networkErrorIndicators = [
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
      'ERR_NETWORK',
      'ERR_INTERNET_DISCONNECTED',
      'ERR_CONNECTION_REFUSED',
      'ERR_CONNECTION_TIMED_OUT',
      'ENOTFOUND',
      'ECONNREFUSED',
      'ETIMEDOUT',
      'AbortError'
    ];

    const errorMessage = error.message || error.toString();
    const errorName = error.name || '';

    return networkErrorIndicators.some(indicator => 
      errorMessage.includes(indicator) || errorName.includes(indicator)
    ) || !this.isOnline;
  }

  /**
   * Execute a function with automatic retry on network errors
   * @param {Function} fn - Async function to execute
   * @param {Object} options - Retry options
   * @returns {Promise} Promise that resolves with the function result
   */
  static async withRetry(fn, options = {}) {
    const {
      maxRetries = this.maxRetries,
      baseDelay = this.retryDelay,
      backoffMultiplier = 2,
      maxDelay = 10000,
      onRetry = null,
      retryCondition = this.isNetworkError
    } = options;

    let lastError;
    let delay = baseDelay;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Wait for network to be online before attempting
        if (!this.isOnline) {
          await this.waitForOnline();
        }

        const result = await fn();
        
        // Success - reset retry count for this function
        if (fn.name) {
          this.retryAttempts.delete(fn.name);
        }
        
        return result;
      } catch (error) {
        lastError = error;

        // Check if we should retry this error
        if (!retryCondition(error) || attempt === maxRetries) {
          throw error;
        }

        // Call retry callback if provided
        if (onRetry) {
          try {
            await onRetry(error, attempt + 1, maxRetries + 1);
          } catch (callbackError) {
            console.error('Error in retry callback:', callbackError);
          }
        }

        // Wait before retrying with exponential backoff
        await this.delay(Math.min(delay, maxDelay));
        delay *= backoffMultiplier;

        console.warn(`Retrying operation (attempt ${attempt + 2}/${maxRetries + 1}) after error:`, error.message);
      }
    }

    throw lastError;
  }

  /**
   * Wait for the network to come back online
   * @param {number} timeout - Maximum time to wait in milliseconds
   * @returns {Promise} Promise that resolves when online or rejects on timeout
   */
  static waitForOnline(timeout = 30000) {
    return new Promise((resolve, reject) => {
      if (this.isOnline) {
        resolve();
        return;
      }

      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout waiting for network connection'));
      }, timeout);

      const cleanup = this.addNetworkListener((status) => {
        if (status === 'online') {
          clearTimeout(timeoutId);
          cleanup();
          resolve();
        }
      });
    });
  }

  /**
   * Delay execution for a specified amount of time
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Promise that resolves after the delay
   */
  static delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Test network connectivity by making a simple request
   * @returns {Promise<boolean>} True if network is accessible
   */
  static async testConnectivity() {
    try {
      // Try to fetch a small resource with a short timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get network information if available
   * @returns {Object} Network information object
   */
  static getNetworkInfo() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    return {
      isOnline: this.isOnline,
      effectiveType: connection?.effectiveType || 'unknown',
      downlink: connection?.downlink || null,
      rtt: connection?.rtt || null,
      saveData: connection?.saveData || false
    };
  }

  /**
   * Create a fetch wrapper with automatic retry and network error handling
   * @param {string} url - URL to fetch
   * @param {Object} options - Fetch options
   * @param {Object} retryOptions - Retry configuration
   * @returns {Promise} Promise that resolves with the fetch response
   */
  static async fetchWithRetry(url, options = {}, retryOptions = {}) {
    return this.withRetry(async () => {
      const controller = new AbortController();
      const timeout = options.timeout || 10000;
      
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    }, retryOptions);
  }
}

/**
 * React hook for network status
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = React.useState(NetworkService.isOnline);
  const [networkInfo, setNetworkInfo] = React.useState(NetworkService.getNetworkInfo());

  React.useEffect(() => {
    const cleanup = NetworkService.addNetworkListener((status, online) => {
      setIsOnline(online);
      setNetworkInfo(NetworkService.getNetworkInfo());
    });

    return cleanup;
  }, []);

  return {
    isOnline,
    networkInfo,
    testConnectivity: NetworkService.testConnectivity,
    waitForOnline: NetworkService.waitForOnline
  };
}

// Export default instance
export default NetworkService;